import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/auth/auth.service';
import { catchError, EMPTY, finalize, map, Observable, switchMap, throwError } from 'rxjs';
import { HttpClient, HttpHeaders, HttpStatusCode } from '@angular/common/http';
import { MessageHandlerService } from 'src/app/components/messages/message-handler/message-handler.service';
import { MessageType } from 'src/app/components/messages/message-handler/message-handler.component';
import { SpinnerProcess } from '../models/spinner-process';
import { TranslateService } from '@ngx-translate/core';
import { LoadWheelService } from 'src/app/components/load-wheel/load-wheel.service';

export type EnumWorksheetOutput = 'scope' | 'schedule' | 'key_needs';

export interface WorksheetKeyNeeds {
   kn?: Record<string, number> | null;
   ekn?: Record<string, number> | null;
   ckn?: Record<string, number> | null;
   dkn_electrical?: Record<string, number> | null;
   dkn_environmental?: Record<string, number> | null;
   flags?: Record<string, number> | null;
   energy?: Record<string, number> | null;
   buy_sell?: Record<string, number> | null;
   material_phase?: Record<string, number> | null;
   [key: string]: any;
}

export interface WorksheetFields {
    pec_activity?: string | null;
    project_status?: string | null;
    afe_date?: string | null;
    rfq_date?: string | null;
    bid_doc_date?: string | null;
    kickoff_date?: string | null;
    completion_date?: string | null;
    tiv?: string | number | null;
    tiv_currency?: string | null;
    tiv_local_amount?: string | number | null;
    tiv_local_currency?: string | null;
    unit_ids?: (number | string)[] | null;
    notes?: string | null;
    project_scope?: string | null;
    project_schedule?: string | null;
    [key: string]: any;
}

export interface WorksheetRequest {
    output: EnumWorksheetOutput;
    project_id: number;
    offline_mode?: boolean;
    plant_id: number;
    sic_product_id: string;
    project_type: string;
    fields?: WorksheetFields;
    selected_key_needs?: WorksheetKeyNeeds;
}

export interface WorksheetResponse {
    output: EnumWorksheetOutput;
    project_scope?: string | null;
    project_schedule?: string | null;
    key_needs?: WorksheetKeyNeeds;
    [key: string]: any;
}

@Injectable({
    providedIn: 'root'
})
export class LlmAppService {

    constructor(
        private readonly http: HttpClient,
        private readonly authService: AuthService,
        private readonly translate: TranslateService,
        private readonly loadWheelService: LoadWheelService,
        private readonly messageHandlerService: MessageHandlerService,
    ) {}

    public getRgcEmbedUrl(): Observable<string> {
        return this.authService.getLlmToken().pipe(map(token => `${environment.llmRgcUrl}?token=${token}`));
    }

    public researchAssistant(rqp: WorksheetRequest): Observable<WorksheetResponse> {
        return this.authService.getLlmToken().pipe(
            switchMap((token: string) => {
                const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant('llmapp.ra.communicating'));
               
                const bodyData = Object.fromEntries(Object.entries(rqp).filter(([, value]) => value != null));
                const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
                return this.http.post<any>(environment.llmRaUrl + '/worksheet', bodyData, { headers }).pipe(
                    catchError(response => {
                        console.error('RA backend response', response);
                        
                        let message = this.translate.instant('llmapp.ra.commFailure', { status: response.status });
                        let messageType = MessageType.ERROR;
                        
                        // Handle a few known/expected errors a bit smoother
                        if (response.status === HttpStatusCode.Unauthorized) {
                            message = this.translate.instant('llmapp.ra.authFailure');
                            this.authService.invalidateLlmTokenCache();
                        } else if ([HttpStatusCode.NotFound, HttpStatusCode.UnprocessableEntity, HttpStatusCode.GatewayTimeout].includes(response.status) && response.error?.detail) {
                            message = this.translate.instant('llmapp.ra.detailMessage', {detail: response.error.detail});
                            messageType = MessageType.WARNING;
                            this.authService.invalidateLlmTokenCache();
                        }
                        
                        this.messageHandlerService.show(message, messageType);
                        return EMPTY;
                    }),
                    finalize(() => {
                        this.loadWheelService.hideWheel(wheel);
                    })
                );
            })
        );
    }

}